import { LoaderFunction, LoaderFunctionArgs, redirect } from 'react-router-dom'

import type { Location } from '@/apiTypes.ts'
import { useAppointmentStore } from '@/store.ts'
import { fetchFromApi } from '@/utils/fetchFromApi.ts'

// 'alphabetical' is the backend default (no sortBy param on the wire); the
// postcode-based distance sort was removed with the postcode finder.
type SortBy = 'alphabetical' | 'availability'

interface LoaderData {
  locations?: Location[]
  error?: string
  loadTime?: number
  sortBy: SortBy
}

const loader: LoaderFunction = async ({
  params,
  request,
}: LoaderFunctionArgs): Promise<LoaderData | Response | null> => {
  const { appointmentType } = params
  const url = new URL(request.url)
  const sortBy: SortBy =
    url.searchParams.get('sortBy') === 'availability'
      ? 'availability'
      : 'alphabetical'

  const { setError } = useAppointmentStore.getState()
  const startTime = Date.now()
  try {
    const apiUrl = `locations?type=${appointmentType}${
      sortBy === 'availability' ? '&sortBy=availability' : ''
    }`
    const response = await fetchFromApi(apiUrl)

    if (!response.ok) {
      // Check for timeout error
      if (response.status === 504) {
        const errorData = await response.json()
        if (errorData.error === 'TIMEFF_TIMEOUT') {
          setError('timeffTimeoutVelthuis')
          return redirect('/error')
        }
      }

      throw new Error('Failed to fetch locations')
    }

    const locations: Location[] = await response.json()
    return {
      loadTime: Date.now() - startTime,
      locations,
      sortBy,
    }
  } catch (error) {
    console.error('Error fetching locations:', error)
    return {
      error:
        error instanceof Error ? error.message : 'An unknown error occurred',
      locations: [],
      sortBy,
    }
  }
}

export type { LoaderData, SortBy }
export { loader }
